/**
 * Accounts — the identity a person can sign back into, and the handle that names what they link.
 *
 * Every visitor already has an anonymous session (src/backend/supabase.ts). Creating an account
 * upgrades THAT session in place: auth.updateUser with an email and password keeps the same
 * auth user id, so the actor and everything already recorded under it carry over. Whether a
 * session is an account is a claim Supabase signs into the JWT (`is_anonymous`), which is what
 * the server's is_account() checks — the client only mirrors it for the UI.
 *
 * Email confirmation: if the project requires it, the address is pending until the person
 * clicks the link, and the session stays anonymous until then. This module reports that as
 * `pendingEmail` so the UI can say "check your inbox" instead of failing; the handle chosen at
 * sign-up is parked in user metadata and claimed on the next load once the account is real.
 *
 * Built as a factory over a narrow client surface so the flows can be proven with a fake.
 */
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { ensureSession, resetSession, supabase } from '../backend/supabase';

export interface Account {
  /** False when the build has no backend at all. */
  configured: boolean;
  /** Signed in with a non-anonymous identity (the server's is_account). */
  isAccount: boolean;
  handle: string | null;
  email: string | null;
  /** An address was set but the confirmation link has not been clicked yet. */
  pendingEmail: string | null;
  cardCount: number;
  /** May read the numbers (stats_daily). Set by hand in the database. */
  isAdmin: boolean;
}

export const NO_BACKEND: Account = { configured: false, isAccount: false, handle: null, email: null, pendingEmail: null, cardCount: 0, isAdmin: false };
export const ANONYMOUS: Account = { configured: true, isAccount: false, handle: null, email: null, pendingEmail: null, cardCount: 0, isAdmin: false };

export type AccountResult =
  | {
      ok: true;
      account: Account;
      needsConfirmation: boolean;
      /** The account exists but the handle asked for was refused (taken, reserved): pick another. */
      handleProblem?: string;
    }
  | { ok: false; reason: string };

/**
 * What stands between this visitor and linking a site, if anything. The server enforces the
 * same rule (submit_link refuses without an account and a handle); this decides which door to
 * show first.
 */
export function linkGate(a: Account): 'none' | 'account' | 'confirm' | 'handle' {
  if (!a.configured) return 'none'; // no backend: the form itself explains that
  if (!a.isAccount) return a.pendingEmail ? 'confirm' : 'account';
  if (!a.handle) return 'handle';
  return 'none';
}

/** Mirrors public.valid_handle + reserved words the server also refuses; the server is the authority. */
export function validateHandle(raw: string): { handle: string } | { error: string } {
  // The field already shows the @; typing it again is natural, not an error.
  const h = raw.trim().replace(/^@+/, '').toLowerCase();
  if (!h) return { error: 'Pick a handle — it is the name on everything you link.' };
  if (h.length < 3) return { error: 'A handle is at least 3 characters.' };
  if (h.length > 20) return { error: 'A handle is at most 20 characters.' };
  if (!/^[a-z0-9_]+$/.test(h)) return { error: 'Lowercase letters, digits and underscores only.' };
  return { handle: h };
}

export function validateEmail(raw: string): { email: string } | { error: string } {
  const e = raw.trim();
  if (!e) return { error: 'Enter your email.' };
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return { error: 'That does not look like an email address.' };
  return { email: e };
}

export function validatePassword(raw: string): { password: string } | { error: string } {
  if (raw.length < 8) return { error: 'A password is at least 8 characters.' };
  if (raw.length > 72) return { error: 'A password is at most 72 characters.' };
  return { password: raw };
}

/** The slice of supabase-js the flows touch; a test hands in a fake with the same shape. */
export interface AccountClient {
  auth: {
    updateUser(attrs: { email?: string; password?: string; data?: Record<string, unknown> }): Promise<{ data: { user: unknown }; error: { message: string } | null }>;
    signInWithPassword(creds: { email: string; password: string }): Promise<{ data: { session: Session | null }; error: { message: string } | null }>;
    refreshSession(): Promise<{ data: { session: Session | null }; error: unknown }>;
    signOut(opts?: { scope: 'local' | 'global' | 'others' }): Promise<{ error: unknown }>;
    resend?(opts: { type: 'signup' | 'email_change'; email: string }): Promise<{ error: { message: string } | null }>;
    resetPasswordForEmail?(email: string, opts?: { redirectTo?: string }): Promise<{ error: { message: string } | null }>;
  };
  rpc(fn: string, args?: Record<string, unknown>): PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>;
}

interface Profile {
  is_account?: boolean;
  handle?: string | null;
  card_count?: number;
  is_admin?: boolean;
}

function sessionUser(s: Session): { isAnonymous: boolean; email: string | null; pendingEmail: string | null; parkedHandle: string | null } {
  const u = s.user as Session['user'] & { is_anonymous?: boolean; new_email?: string | null };
  const confirmed = Boolean(u.email_confirmed_at);
  const isAnonymous = u.is_anonymous === true;
  const pending = u.new_email ?? (u.email && !confirmed ? u.email : null);
  const meta = (u.user_metadata ?? {}) as { handle?: unknown };
  return {
    isAnonymous,
    email: confirmed && u.email ? u.email : null,
    pendingEmail: isAnonymous && pending ? pending : null,
    parkedHandle: typeof meta.handle === 'string' ? meta.handle : null,
  };
}

export function createAccountApi(client: AccountClient | null, resolve: () => Promise<Session | null>, reset: () => void = () => {}) {
  async function claim(handle: string): Promise<{ ok: true; handle: string } | { ok: false; reason: string }> {
    if (!client) return { ok: false, reason: NO_BACKEND_REASON };
    const v = validateHandle(handle);
    if ('error' in v) return { ok: false, reason: v.error };
    const { data, error } = await client.rpc('claim_handle', { p_handle: v.handle });
    if (error) return { ok: false, reason: error.message || 'Could not save the handle.' };
    const r = (data ?? {}) as { ok?: boolean; handle?: string; reason?: string };
    return r.ok && r.handle ? { ok: true, handle: r.handle } : { ok: false, reason: r.reason ?? 'Could not save the handle.' };
  }

  async function load(): Promise<Account> {
    if (!client) return NO_BACKEND;
    const s = await resolve();
    if (!s) return ANONYMOUS;
    const u = sessionUser(s);
    let profile: Profile = {};
    let profileLoaded = false;
    try {
      const { data, error } = await client.rpc('my_profile');
      if (!error && data && typeof data === 'object') {
        profile = data as Profile;
        profileLoaded = true;
      }
    } catch {
      /* the session still counts; the profile is just unknown */
    }
    const isAccount = profile.is_account ?? !u.isAnonymous;
    let handle = profile.handle ?? null;
    // The handle chosen at sign-up, claimed now that the account is real — but only when the
    // profile was actually read and says there is none. A failed read must not turn a parked
    // name into a rename of the handle the person already has.
    if (profileLoaded && isAccount && !handle && u.parkedHandle) {
      const c = await claim(u.parkedHandle);
      if (c.ok) handle = c.handle;
    }
    return { configured: true, isAccount, handle, email: u.email, pendingEmail: u.pendingEmail, cardCount: profile.card_count ?? 0, isAdmin: profile.is_admin === true };
  }

  async function create(input: { email: string; password: string; handle: string }): Promise<AccountResult> {
    if (!client) return { ok: false, reason: NO_BACKEND_REASON };
    const e = validateEmail(input.email);
    if ('error' in e) return { ok: false, reason: e.error };
    const p = validatePassword(input.password);
    if ('error' in p) return { ok: false, reason: p.error };
    const h = validateHandle(input.handle);
    if ('error' in h) return { ok: false, reason: h.error };

    const s = await resolve();
    if (!s) return { ok: false, reason: 'Could not start a session — try again in a moment.' };
    if (!sessionUser(s).isAnonymous) return { ok: false, reason: 'You are already signed in.' };

    // Upgrade in place: same auth user, same actor. The handle rides in metadata so it can be
    // claimed after an email confirmation, which may happen on a later visit.
    const { error } = await client.auth.updateUser({ email: e.email, password: p.password, data: { handle: h.handle } });
    if (error) return { ok: false, reason: friendlyAuthError(error.message) };
    await client.auth.refreshSession();
    reset();
    const account = await load();
    if (!account.isAccount) {
      return { ok: true, account: { ...account, pendingEmail: account.pendingEmail ?? e.email }, needsConfirmation: true };
    }
    if (!account.handle) {
      const c = await claim(h.handle);
      // The account exists either way; a refused handle is picked again in the profile view.
      if (!c.ok) return { ok: true, account, needsConfirmation: false, handleProblem: c.reason };
      return { ok: true, account: { ...account, handle: c.handle }, needsConfirmation: false };
    }
    return { ok: true, account, needsConfirmation: false };
  }

  async function signIn(input: { email: string; password: string }): Promise<AccountResult> {
    if (!client) return { ok: false, reason: NO_BACKEND_REASON };
    const e = validateEmail(input.email);
    if ('error' in e) return { ok: false, reason: e.error };
    if (!input.password) return { ok: false, reason: 'Enter your password.' };
    const { error } = await client.auth.signInWithPassword({ email: e.email, password: input.password });
    if (error) return { ok: false, reason: friendlyAuthError(error.message) };
    reset();
    const account = await load();
    return { ok: true, account, needsConfirmation: !account.isAccount };
  }

  async function signOut(): Promise<Account> {
    if (!client) return NO_BACKEND;
    // This device only. The default scope revokes every device's session, which would leave a
    // phone with a dead cached session and telemetry piling up behind it.
    await client.auth.signOut({ scope: 'local' });
    reset();
    return load(); // a fresh anonymous session, so the feed keeps working
  }

  /**
   * Re-read who this is from the server, not the cache: after an email confirmation in another
   * tab or on another device, the account exists but this tab's token still says anonymous
   * until it is refreshed (up to an hour on its own).
   */
  async function refresh(): Promise<Account> {
    if (!client) return NO_BACKEND;
    try {
      await client.auth.refreshSession();
    } catch {
      /* offline: load() reports what the cached token says */
    }
    reset();
    return load();
  }

  /** Send the confirmation email again for the address that is pending. */
  async function resend(): Promise<{ ok: true } | { ok: false; reason: string }> {
    if (!client) return { ok: false, reason: NO_BACKEND_REASON };
    const s = await resolve();
    const pending = s ? sessionUser(s).pendingEmail : null;
    if (!pending) return { ok: false, reason: 'There is no email waiting to be confirmed.' };
    if (!client.auth.resend) return { ok: false, reason: 'Resending is not available in this build.' };
    // An anonymous user adding an email is an email CHANGE in Supabase's eyes.
    const { error } = await client.auth.resend({ type: 'email_change', email: pending });
    if (error) return { ok: false, reason: friendlyAuthError(error.message) };
    return { ok: true };
  }

  /**
   * A forgotten password: Supabase emails a link that signs the person in on /reset, where they
   * choose a new one. The answer never says whether the address has an account — neither does
   * Supabase — so a stranger cannot use this form to learn who is here.
   */
  async function requestReset(email: string, redirectTo: string): Promise<{ ok: true } | { ok: false; reason: string }> {
    if (!client) return { ok: false, reason: NO_BACKEND_REASON };
    const e = validateEmail(email);
    if ('error' in e) return { ok: false, reason: e.error };
    if (!client.auth.resetPasswordForEmail) return { ok: false, reason: 'Resetting a password is not available in this build.' };
    const { error } = await client.auth.resetPasswordForEmail(e.email, { redirectTo });
    if (error) return { ok: false, reason: friendlyAuthError(error.message) };
    return { ok: true };
  }

  /** The new password, for the account the reset link signed in (or any signed-in account). */
  async function setPassword(password: string): Promise<AccountResult> {
    if (!client) return { ok: false, reason: NO_BACKEND_REASON };
    const p = validatePassword(password);
    if ('error' in p) return { ok: false, reason: p.error };
    const s = await resolve();
    if (!s || sessionUser(s).isAnonymous) return { ok: false, reason: 'That link has expired or was already used. Send yourself a new one.' };
    const { error } = await client.auth.updateUser({ password: p.password });
    if (error) return { ok: false, reason: friendlyAuthError(error.message) };
    reset();
    const account = await load();
    return { ok: true, account, needsConfirmation: false };
  }

  return { load, create, signIn, signOut, claim, refresh, resend, requestReset, setPassword };
}

const NO_BACKEND_REASON = 'This build has no backend, so accounts cannot be created.';

function friendlyAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('already registered') || m.includes('already been registered') || m.includes('already exists')) {
    return 'That email already has an account — sign in instead.';
  }
  if (m.includes('invalid login') || m.includes('invalid credentials')) {
    // Also what a not-yet-confirmed sign-up gets on a second device: the address is not on
    // the account until the link is opened.
    return 'Wrong email or password. If you just created the account, open the confirmation link first.';
  }
  if (m.includes('email not confirmed')) return 'Confirm your email first — the link is in your inbox.';
  if (m.includes('rate limit') || m.includes('too many')) return 'Too many attempts — try again in a minute.';
  if (m.includes('password')) return message;
  return message || 'The server refused that.';
}

/** The app's account API, bound to the configured client. */
export const account = createAccountApi(supabase as unknown as AccountClient | null, ensureSession, resetSession);
